import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, KeyRound, Smartphone, RefreshCw, AlertOctagon, Copy, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { api, apiErrorText } from "@/lib/api";
import {
  VLKPageHeader, VLKCard, VLKButton, VLKInput, VLKLabel, VLKAlertDialog, toast,
} from "@/components/vlk";

/**
 * Security settings — MFA (TOTP) enrollment, verification, disable, backup codes.
 *
 * All MFA state lives server-side; this page renders the appropriate step by
 * fetching /api/auth/mfa/status.
 */
export default function Security() {
  const qc = useQueryClient();
  const { data: status, isLoading, refetch } = useQuery({
    queryKey: ["mfa", "status"],
    queryFn: () => api.get("/auth/mfa/status").then((r) => r.data),
  });

  const [enrollment, setEnrollment] = useState(null);   // {provisioning_uri, secret, qr_png_b64}
  const [confirmCode, setConfirmCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);

  const enroll = useMutation({
    mutationFn: () => api.post("/auth/mfa/enroll").then((r) => r.data),
    onSuccess: (d) => setEnrollment(d),
    onError: (e) => toast.error("Enrollment failed", { description: apiErrorText(e) }),
  });

  const confirm = useMutation({
    mutationFn: () => api.post("/auth/mfa/enroll/confirm", { code: confirmCode.trim() }).then((r) => r.data),
    onSuccess: (d) => {
      setBackupCodes(d.backup_codes);
      setEnrollment(null);
      setConfirmCode("");
      qc.invalidateQueries({ queryKey: ["mfa"] });
      toast.success("Multi-factor enabled");
    },
    onError: (e) => toast.error("Wrong code", { description: apiErrorText(e) }),
  });

  return (
    <div data-testid="security-page">
      <VLKPageHeader
        title="Security"
        description="Multi-factor authentication protects your VeraLok operator account. Enrol a time-based authenticator (Authy, 1Password, Google Authenticator, etc.) and save the backup codes offline."
      />

      {isLoading ? (
        <div className="p-10 flex items-center justify-center text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
          <VLKCard>
            <div className="p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className={cn("h-10 w-10 rounded-sm border flex items-center justify-center",
                                    status?.enabled ? "border-success/40 text-success bg-success/5" : "border-border text-muted-foreground")}>
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display text-lg">Two-factor authentication</div>
                  <div className="text-xs text-muted-foreground mt-0.5" data-testid="mfa-state-label">
                    {status?.enabled ? `Enabled · ${status.backup_codes_remaining} backup codes remaining` : "Disabled — anyone with your password can sign in."}
                  </div>
                </div>
                <div className="ml-auto">
                  {status?.enabled ? (
                    <div className="flex gap-2">
                      <VLKButton variant="outline" size="md" data-testid="mfa-regen-btn" onClick={() => setRegenOpen(true)}>
                        <RefreshCw className="h-4 w-4" /> Regenerate codes
                      </VLKButton>
                      <VLKButton variant="destructive" size="md" data-testid="mfa-disable-btn" onClick={() => setDisableOpen(true)}>
                        Disable
                      </VLKButton>
                    </div>
                  ) : !enrollment ? (
                    <VLKButton size="md" data-testid="mfa-enroll-btn" loading={enroll.isPending} onClick={() => enroll.mutate()}>
                      <Smartphone className="h-4 w-4" /> Enable 2FA
                    </VLKButton>
                  ) : null}
                </div>
              </div>
            </div>

            {enrollment && (
              <div className="p-5 space-y-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Step 1 · Scan the QR</div>
                <div className="flex gap-6 items-start">
                  <img src={`data:image/png;base64,${enrollment.qr_png_b64}`} alt="MFA QR" className="h-40 w-40 border border-border rounded-sm" data-testid="mfa-qr" />
                  <div className="text-xs space-y-2">
                    <p>Point your authenticator app at this QR code. If you cannot scan, enter this secret manually:</p>
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-xs bg-secondary/40 px-2 py-1 rounded-sm" data-testid="mfa-secret">{enrollment.secret}</code>
                      <button className="text-muted-foreground hover:text-foreground" onClick={() => { navigator.clipboard.writeText(enrollment.secret); toast.success("Copied"); }}>
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="text-muted-foreground">Issuer: VeraLok · Algorithm: SHA-1 · Digits: 6 · Period: 30s</p>
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Step 2 · Confirm with a live code</div>
                  <div className="flex gap-2">
                    <VLKInput
                      data-testid="mfa-confirm-code"
                      value={confirmCode}
                      onChange={(e) => setConfirmCode(e.target.value)}
                      placeholder="123 456"
                      className="max-w-[200px] font-mono"
                    />
                    <VLKButton data-testid="mfa-confirm-btn" loading={confirm.isPending} disabled={confirmCode.trim().length < 6} onClick={() => confirm.mutate()}>
                      Confirm & enable
                    </VLKButton>
                    <VLKButton variant="ghost" onClick={() => { setEnrollment(null); setConfirmCode(""); }}>Cancel</VLKButton>
                  </div>
                </div>
              </div>
            )}

            {backupCodes && (
              <div className="p-5 border-t border-warning/30 bg-warning/5">
                <div className="flex items-start gap-3">
                  <AlertOctagon className="h-5 w-5 text-warning shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-warning">Save these backup codes now</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Each code works once. Store them offline — you will NOT see them again.</div>
                    <ul className="mt-3 grid grid-cols-2 gap-2 text-sm font-mono" data-testid="mfa-backup-codes">
                      {backupCodes.map((c) => (
                        <li key={c} className="border border-warning/30 rounded-sm px-2 py-1 bg-background">{c}</li>
                      ))}
                    </ul>
                    <div className="mt-3 flex gap-2">
                      <VLKButton variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Copied to clipboard"); }}>
                        <Copy className="h-3.5 w-3.5" /> Copy all
                      </VLKButton>
                      <VLKButton variant="ghost" size="sm" onClick={() => setBackupCodes(null)}>I've saved them</VLKButton>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </VLKCard>

          <VLKCard>
            <div className="p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Why 2FA</div>
              <p className="text-xs leading-relaxed">A stolen password alone cannot sign into VeraLok when 2FA is enabled. This is required for all operator roles reviewing government or financial identity flows.</p>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground pt-3">Recommended apps</div>
              <ul className="text-xs space-y-0.5 text-muted-foreground">
                <li>· 1Password</li>
                <li>· Authy</li>
                <li>· Google Authenticator</li>
                <li>· Microsoft Authenticator</li>
              </ul>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground pt-3">Coming soon</div>
              <p className="text-xs text-muted-foreground">WebAuthn (FIDO2) hardware keys and platform biometrics.</p>
            </div>
          </VLKCard>
        </div>
      )}

      {disableOpen && <DisableDialog onClose={() => setDisableOpen(false)} onDone={() => { setDisableOpen(false); refetch(); }} />}
      {regenOpen && <RegenDialog onClose={() => setRegenOpen(false)} onDone={(codes) => { setBackupCodes(codes); setRegenOpen(false); refetch(); }} />}
    </div>
  );
}

function DisableDialog({ onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const mut = useMutation({
    mutationFn: () => api.post("/auth/mfa/disable", { password, code: code.trim() }).then((r) => r.data),
    onSuccess: () => { toast.success("2FA disabled"); onDone(); },
    onError: (e) => toast.error("Could not disable", { description: apiErrorText(e) }),
  });
  return (
    <VLKAlertDialog open onOpenChange={(v) => !v && onClose()} title="Disable two-factor authentication"
      actions={<>
        <VLKButton variant="ghost" onClick={onClose}>Cancel</VLKButton>
        <VLKButton variant="destructive" data-testid="mfa-disable-submit" loading={mut.isPending}
                    disabled={!password || code.trim().length < 6} onClick={() => mut.mutate()}>
          Disable
        </VLKButton>
      </>}
    >
      <div className="space-y-3">
        <p className="text-xs text-warning">You will be able to sign in with password alone. Only proceed if you understand the impact.</p>
        <div><VLKLabel>Password</VLKLabel><VLKInput type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="mfa-disable-password" /></div>
        <div><VLKLabel>Current 2FA code or backup code</VLKLabel><VLKInput value={code} onChange={(e) => setCode(e.target.value)} data-testid="mfa-disable-code" /></div>
      </div>
    </VLKAlertDialog>
  );
}

function RegenDialog({ onClose, onDone }) {
  const [code, setCode] = useState("");
  const mut = useMutation({
    mutationFn: () => api.post("/auth/mfa/backup-codes/regenerate", { code: code.trim() }).then((r) => r.data),
    onSuccess: (d) => onDone(d.backup_codes),
    onError: (e) => toast.error("Could not regenerate", { description: apiErrorText(e) }),
  });
  return (
    <VLKAlertDialog open onOpenChange={(v) => !v && onClose()} title="Regenerate backup codes"
      actions={<>
        <VLKButton variant="ghost" onClick={onClose}>Cancel</VLKButton>
        <VLKButton data-testid="mfa-regen-submit" loading={mut.isPending} disabled={code.trim().length < 6} onClick={() => mut.mutate()}>
          Regenerate
        </VLKButton>
      </>}
    >
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">All existing backup codes will be invalidated. Enter a current 2FA code to authorise.</p>
        <VLKInput value={code} onChange={(e) => setCode(e.target.value)} placeholder="123 456" data-testid="mfa-regen-code" />
      </div>
    </VLKAlertDialog>
  );
}
